import React, { useEffect, useState } from "react";
import { blogsApi, getImageUrl } from "../api";
import ImageLoader from "./common/ImageLoader";
import "./AdminBlogs.css";

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [title, setTitle] = useState("");
  const [abstract, setAbstract] = useState("");
  const [date, setDate] = useState("");
  const [poster, setPoster] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const res = await blogsApi.getAll();
      setBlogs(res.data || []);
    } catch (err) {
      console.error("Fetch blogs error:", err);
    }
  };

  const handlePosterChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPoster(file);
      setPosterPreview(URL.createObjectURL(file));
    }
  };

  const handlePdfChange = (e) => {
    setPdf(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !abstract || !date || !poster || !pdf) {
      return alert("All fields including Title, Abstract, Date, Poster, and PDF are required!");
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("title", title);
    formData.append("abstract", abstract);
    formData.append("date", date);
    formData.append("poster", poster);
    formData.append("pdf", pdf);

    try {
      await blogsApi.create(formData);
      alert("✅ Blog article published successfully!");
      setTitle("");
      setAbstract("");
      setDate("");
      setPoster(null);
      setPosterPreview(null);
      setPdf(null);
      e.target.reset();
      fetchBlogs();
    } catch (err) {
      console.error("Upload error:", err);
      alert("❌ Upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this blog article permanently?")) return;
    try {
      await blogsApi.delete(id);
      alert("🗑️ Blog deleted!");
      fetchBlogs();
    } catch (err) {
      console.error("Delete error:", err);
      alert("❌ Delete failed.");
    }
  };

  return (
    <div className="admin-blogs-wrapper">
      <div className="admin-panel-header">
        <div className="panel-title-group">
          <i className="fas fa-feather-alt title-icon"></i>
          <div>
            <h2>Manage Chapter Blogs &amp; Articles</h2>
            <p className="panel-subtitle">Publish student articles, research summaries, and technical whitepapers</p>
          </div>
        </div>
      </div>

      {/* Upload Form */}
      <form onSubmit={handleSubmit} className="event-form-card">
        <h3 className="sub-heading-tag"><i className="fas fa-edit"></i> Write / Publish New Blog</h3>

        <div className="event-form-grid">
          <div className="admin-form-group">
            <label>Blog Article Title *</label>
            <input
              type="text"
              placeholder="e.g. Exploring Generative AI Paradigms"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="admin-form-group">
            <label><i className="far fa-calendar-alt"></i> Publication Date *</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div className="admin-form-group span-2">
            <label>Abstract / Executive Summary *</label>
            <textarea
              placeholder="Provide a concise summary of key findings and discussion points..."
              value={abstract}
              onChange={(e) => setAbstract(e.target.value)}
              rows="4"
              required
            />
          </div>

          <div className="admin-form-group">
            <label><i className="fas fa-image"></i> Blog Cover Poster *</label>
            <input type="file" accept="image/*" onChange={handlePosterChange} required />
            {posterPreview && (
              <div className="poster-upload-preview">
                <img src={posterPreview} alt="Cover Preview" />
              </div>
            )}
          </div>

          <div className="admin-form-group">
            <label><i className="far fa-file-pdf"></i> Full Article PDF Document *</label>
            <input type="file" accept="application/pdf" onChange={handlePdfChange} required />
          </div>
        </div>

        <button type="submit" className="admin-btn-primary submit-event-btn" disabled={loading}>
          {loading ? (
            <>
              <i className="fas fa-circle-notch fa-spin"></i> Publishing...
            </>
          ) : (
            <>
              <i className="fas fa-paper-plane"></i> Publish Blog Post
            </>
          )}
        </button>
      </form>

      {/* Blog Cards Grid */}
      <div className="blogs-feed-section">
        <h3 className="sub-heading-tag">
          <i className="fas fa-book-open"></i> Published Articles ({blogs.length})
        </h3>

        {blogs.length === 0 ? (
          <p className="no-items-text">No blogs published yet.</p>
        ) : (
          <div className="admin-blogs-grid">
            {blogs.map((b) => (
              <div key={b.id} className="admin-blog-card">
                <div className="blog-poster-frame">
                  <ImageLoader src={getImageUrl(b.poster)} alt={b.title} />
                  <span className="blog-date-chip">
                    <i className="far fa-calendar-alt"></i> {b.date}
                  </span>
                </div>

                <div className="blog-content-body">
                  <h4>{b.title}</h4>
                  <p className="blog-abstract-text">{b.abstract}</p>

                  <div className="blog-card-bottom">
                    <a
                      href={getImageUrl(b.pdf)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="read-pdf-btn"
                    >
                      <i className="far fa-file-pdf"></i> Read PDF
                    </a>

                    <button
                      className="delete-ev-icon-btn"
                      onClick={() => handleDelete(b.id)}
                      title="Delete blog"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBlogs;